import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const markerEmphasisSkill: Skill = {
    name: SKILL_NAMES.markerEmphasis,
    kind: 'codeo',
    description:
        'Mark up a key word the hand-drawn way as it is spoken — a marker highlight sweep behind ' +
        'it, a drawn circle around it, or a cross-out over an old value. One mark per moment.',
    instructions: `# Marker Emphasis

The explainer-video vocabulary for emphasis: a marker sweeping in behind a word, a hand-drawn circle scribbled around it, a line crossing out the old price. It reads as a person reaching in and marking what matters — warmer and more characterful than a scale-and-color pop (\`${SKILL_NAMES.emphasisWordPulse}\`, which this complements rather than replaces). Land the mark on the word's spoken onset.

## Markup

The marked word and its mark share a relatively-positioned wrapper so the mark tracks the word. The word carries a unique \`id\`; the mark elements are pure decoration.

\`\`\`html
<span class="mark-wrap" style="position: relative; display: inline-block; font-family: var(--l5-font-0), sans-serif; font-size: 72px; color: var(--l5-color-0, #101820);">
  <span class="mark-bar" style="position: absolute; left: -6px; right: -6px; top: 8%; bottom: 8%;
        background: var(--l5-color-1, #ffd24a); opacity: 0.32; border-radius: 3px; z-index: 0;
        transform: scaleX(0); transform-origin: left center;"></span>
  <span id="kw-mark" style="position: relative; z-index: 1;">unbeatable</span>
</span>
\`\`\`

The mark-bar's initial \`scaleX(0)\` is its resting from-state, left in CSS; the GSAP tween only animates the scale channel up from there via a \`to\` (which is allowed on a CSS-transformed element).

## Timeline code

\`ONSET\` is the word's spoken second, read straight off the scene's \`cues:\` line in the video state, where each word is prefixed \`[N.Ns]\`.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const ONSET = 0.5;
// Highlight sweep — bar wipes in behind the word, landing on the onset
tl.to('.mark-bar', { scaleX: 1, duration: 0.45, ease: 'power2.out' }, ONSET - 0.2);
window.__timelines = { main: tl };
\`\`\`

Drawn circle (variant): an absolutely-positioned ring (\`width: 130%; height: 160%\`, \`border: 3px solid var(--l5-color-1, #ffd24a)\`, \`border-radius: 50%\`, slight \`rotate(-3deg)\`) with a resting \`scale(0)\` in CSS, scaled in with a wobble — \`tl.to('#circle', { scale: 1, duration: 0.6, ease: 'back.out(1.7)', transformOrigin: 'center center' }, ONSET)\`.

Cross-out (variant): for crossing out an old price/claim, two thin bars (resting \`scaleX(0)\` in CSS, rotated ±12°, \`transform-origin: left center\`) drawn one after the other — \`tl.to('#strike-fwd', { scaleX: 1, duration: 0.3 }, t).to('#strike-bwd', { scaleX: 1, duration: 0.3 }, t + 0.15)\`.

## Adapt

- **Highlight**: 0.32 opacity reads as marker ink; full opacity hides the text. A 1–2° \`skewX\` on the bar sells the hand-drawn feel. Multi-line — stagger bars (\`stagger: 0.3\`) so the marker travels line to line.
- **Circle**: \`back.out(1.7)\` gives the overshoot a real pen has. Widen to an ellipse for long words, tighten for short ones.
- **Cross-out**: pair with a fresh value entering — strike the old, then reveal the new beside or below it.
- If the scene marks more than one beat, cycle modes across them (highlight → circle → cross-out) for variety — but still only one mark per beat so the eye knows where to look.

## Pitfalls

- One emphasis mark per moment. Two marks on screen at once cancel each other out — nothing reads as "the" point.
- The mark elements hold no text (they're decoration); the *word* is the text leaf and carries the \`id\`.
- Keep the highlight color translucent (an accent token); an opaque slab over text is a redaction bar, not a highlight.
- Mark a word that's already revealed and readable — sweeping a highlight behind a word that hasn't entered yet marks empty space.
- Animate the mark's scale with a \`to\` tween (its \`scaleX(0)\`/\`scale(0)\` resting transform is the from-state). Don't also bake a competing translate \`transform\` into it, and don't drive it with \`from\`/\`set\` on the transform channel — that conflicts with the CSS transform.`,
};
